import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Tab,
  Table,
  Button,
  Dropdown,
  Modal,
  Input,
  Pagination,
  Icon,
  Grid,
} from "semantic-ui-react";

import moment from "moment";

import DateTextBox from "../common/DateTextBox";
import TimeTextBox from "../common/TimeTextBox";

import "./CardPaymentSlip.scss";

import { parseBeOrAdDate } from "react-lib/utils/dateUtils";

type CardPaymentSlipProps = {
  buttonLoadCheck?: any;
  onEvent?: any;
  appointmentPaymentRelationList?: any;
  userDetail?: any;
  appointmentPaymentAction?: any;
};

const ICON = {
  IconUpload: "/static/images/icon_upload_payment.png",
};

const COLOR = {
  primary: "var(--primary-theme-color)",
  light_pink: "#ffd8d8",
  red: "#d31919",
  green: "#06813d",
  dark_green: "#28922c",
  dark_blue: "#0c5bcc",
  light_orange: "#ffd7af",
  orange: "#d77b24",
  light_green: "#c4eac5",
  gray: "#828282",
};

export const STATUS_OPTIONS = [
  { key: 3, value: "CONFIRM", text: "ชำระแล้ว" },
  { key: 5, value: "REJECT", text: "หลักฐานมีปัญหา" },
  { key: 9, value: "REFUND", text: "คืนเงินผู้ป่วยบางส่วน" },
];

export const REFUND_STATUS_OPTIONS = [
  { key: 7, value: "ACCEPT_REQUEST_REFUND", text: "ยอมรับคำขอคืนเงิน" },
  { key: 9, value: "REFUND", text: "คืนเงินสำเร็จ" },
  { key: 8, value: "REJECT_REQUEST_REFUND", text: "ปฏิเสธคำขอคืนเงิน" },
];

const ROWS_PER_PAGE = 15;

const boxStyle: React.CSSProperties = {
  backgroundColor: "#d9d9d9",
  borderRadius: "4px",
  padding: "0.3rem 1rem",
  display: "inline-block",
  width: "600px",
  wordBreak: "break-word",
  whiteSpace: "normal",
  fontSize: "20px",
  textAlign: "right",
};

const labelStyle: React.CSSProperties = {
  display: "inline-block",
  width: "700px",
  fontWeight: "bold",
  verticalAlign: "top",
  fontSize: "20px",
};

const fieldContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  marginBottom: "1rem",
};

interface CustomTableProps extends CardPaymentSlipProps {
  data: any[];
  emptyRows: any[];
  status?:
    | "REQUESTED"
    | "REQUESTED_CONFIRM"
    | "CONFIRMED"
    | "REJECTED"
    | "REQUESTED_REFUND"
    | "REFUNDING"
    | "REFUNDED";
  buttonLoadCheck?: any;
  onEvent?: any;
  appointmentPaymentRelationList?: any[];
  userDetail?: any;
}

const CustomTable = forwardRef<unknown, CustomTableProps>(
  (
    {
      data,
      emptyRows,
      status,
      buttonLoadCheck,
      onEvent,
      appointmentPaymentRelationList,
      userDetail,
    },
    ref
  ) => {
    const [userDetailsMap, setUserDetailsMap] = useState<{ [key: number]: any }>({});
    const [openSlipPreview, setOpenSlipPreview] = useState(false);
    const [selectedSlipUrl, setSelectedSlipUrl] = useState("");
    const [zoom, setZoom] = useState(1);
    const [selectedPayment, setSelectedPayment] = useState<any>(null);
    const [openSlipDetail, setOpenSlipDetail] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [rejectReasonText, setRejectReasonText] = useState<string>("");
    const [openRefundDetail, setOpenRefundDetail] = useState(false);
    const [selectedRefundStatus, setSelectedRefundStatus] = useState<string | null>(null);
    const [refundDate, setRefundDate] = useState("");
    const [refundTime, setRefundTime] = useState("");
    const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
    const [resizing, setResizing] = useState<{
      columnKey: string | null;
      startX: number;
      startWidth: number;
    }>({
      columnKey: null,
      startX: 0,
      startWidth: 0,
    });

    const isRequested = status === "REQUESTED";
    const isConfirm = status === "REQUESTED_CONFIRM";
    const isNotChecked = isRequested || isConfirm;
    const isRefunding =
      status === "REQUESTED_REFUND" ||
      status === "REFUNDING" ||
      selectedPayment?.patient_appointment?.status === 5;
    const addAction = isConfirm || isRefunding;
    const isRefunded = status === "REFUNDED";

    const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
    const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.5));
    const fetchedUserIdsRef = useRef<Set<number | string>>(new Set());

    const ResizableHeaderCell: React.FC<{ columnKey: string; children: React.ReactNode }> = ({
      columnKey,
      children,
    }) => {
      return (
        <Table.HeaderCell
          style={{
            width: columnWidths[columnKey] || "auto",
            position: "relative",
            userSelect: "none",
            whiteSpace: "nowrap",
            height: "50px",
          }}
        >
          {children}
          <div
            onMouseDown={(e) => handleMouseDown(e, columnKey)}
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              height: "50px",
              width: "6px",
              cursor: "col-resize",
              zIndex: 10,
              userSelect: "none",
              background: "transparent",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Table.HeaderCell>
      );
    };

    const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth =
        columnWidths[columnKey] ||
        e.currentTarget.parentElement?.getBoundingClientRect().width ||
        150;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      setResizing({ columnKey, startX, startWidth });
    };

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!resizing.columnKey) return;

        e.preventDefault();

        const deltaX = e.clientX - resizing.startX;
        const newWidth = Math.max(60, resizing.startWidth + deltaX);

        setColumnWidths((prev) => ({
          ...prev,
          [resizing.columnKey!]: newWidth,
        }));
      };

      const handleMouseUp = () => {
        setResizing({ columnKey: null, startX: 0, startWidth: 0 });
        document.body.style.cursor = "default";
        document.body.style.userSelect = "auto";
      };

      if (resizing.columnKey) {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
      }

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "default";
        document.body.style.userSelect = "auto";
      };
    }, [resizing]);

    useEffect(() => {
      if (appointmentPaymentRelationList && appointmentPaymentRelationList?.length > 0) {
        const uniqueEditUserIds = [
          ...new Set(
            appointmentPaymentRelationList
              .map((p: any) => p?.edit_user)
              .filter((id: any) => id !== null && id !== undefined)
          ),
        ];

        if (uniqueEditUserIds.length === 0) {
          return;
        }

        const missingUserIds = uniqueEditUserIds.filter(
          (id) => !userDetailsMap[id] && !fetchedUserIdsRef.current.has(id)
        );

        if (missingUserIds.length === 0) {
          return;
        }

        missingUserIds.forEach((userId) => {
          if (typeof userId === "number" || typeof userId === "string") {
            fetchedUserIdsRef.current.add(userId);
            console.log("📡 Fetching user detail for ID:", userId);
            onEvent({
              message: "HandleGetUserDetail",
              params: { pk: userId },
            });
          } else {
            console.error("Invalid userId type detected:", userId);
          }
        });
      } else {
        console.log("appointmentPaymentRelationList is empty or undefined");
      }
    }, [appointmentPaymentRelationList, userDetailsMap]);

    useEffect(() => {
      if (userDetail && userDetail.id) {
        setUserDetailsMap((prev) => ({
          ...prev,
          [userDetail.id]: userDetail,
        }));
      }
    }, [userDetail]);

    useEffect(() => {
      if (buttonLoadCheck?.postPaymentAction === "SUCCESS") {
        setOpenSlipDetail(false);
        setSelectedStatus("");
        setRejectReasonText("");

        setOpenRefundDetail(false);
        setSelectedRefundStatus("");
        setRefundDate("");
        setRefundTime("");
      }
    }, [buttonLoadCheck?.postPaymentAction]);

    useImperativeHandle(ref, () => ({
      resetColumnWidths: () => {
        setColumnWidths({});
      },
    }));

    return (
      <div
        style={{
          overflowX: "auto",
          width: "100%",
          display: "block",
        }}
      >
        <Table
          celled
          structured
          selectable
          style={{
            textAlign: "center",
            width: "max-content",
            minWidth: "100%",
            tableLayout: "fixed",
          }}
        >
          <Table.Header>
            <Table.Row>
              <ResizableHeaderCell columnKey="name">ชื่อผู้รับบริการ</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="phone">เบอร์โทรติดต่อ</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="email">อีเมล์</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="address">สถานที่รับบริการ</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="created">วันที่ทำรายการนัดหมาย</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="dueDate">
                วันที่และเวลาครบกำหนดชำระ
              </ResizableHeaderCell>
              <ResizableHeaderCell columnKey="service">ชื่อบริการ</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="price">ราคา</ResizableHeaderCell>
              {!isRequested && (
                <ResizableHeaderCell columnKey="paid">
                  วันที่และเวลาที่ผู้รับบริการโอน
                </ResizableHeaderCell>
              )}
              {!isRequested && (
                <ResizableHeaderCell columnKey="slip">หลักฐานการชำระเงิน</ResizableHeaderCell>
              )}
              {!isRequested && (
                <ResizableHeaderCell columnKey="status">สถานะการตรวจสอบ</ResizableHeaderCell>
              )}
              {!isNotChecked && (
                <ResizableHeaderCell columnKey="checked">วันที่ตรวจสอบหลักฐาน</ResizableHeaderCell>
              )}
              {status === "REJECTED" && (
                <ResizableHeaderCell columnKey="reason">สาเหตุ</ResizableHeaderCell>
              )}
              {(isRefunding || isRefunded) && (
                <>
                  <ResizableHeaderCell columnKey="requestRefund">
                    วันที่ขอคืนเงิน
                  </ResizableHeaderCell>
                  <ResizableHeaderCell columnKey="cancelReason">
                    สาเหตุการยกเลิกทำรายการ
                  </ResizableHeaderCell>
                </>
              )}
              {isRefunded && (
                <>
                  <ResizableHeaderCell columnKey="refundDate">วันที่โอนเงินคืน</ResizableHeaderCell>
                  <ResizableHeaderCell columnKey="refundSlip">
                    หลักฐานการคืนเงิน
                  </ResizableHeaderCell>
                </>
              )}
              {addAction && <ResizableHeaderCell columnKey="action">action</ResizableHeaderCell>}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {data.map((payment: any) => {
              const user = userDetailsMap[payment.patient_appointment?.edit_user];
              const fileUrl = payment.payment_confirmation;
              const fileName = fileUrl ? fileUrl.split("/").pop().split("?")[0] : null;

              console.log("status", payment.payment_appointment?.status, payment.status);

              return (
                <Table.Row key={payment.id}>
                  <Table.Cell style={{ textAlign: "left" }}>
                    {`${payment.patient_appointment?.patient_first_name ?? ""} ${
                      payment.patient_appointment?.patient_last_name ?? ""
                    }`}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: "left" }}>
                    {payment.patient_appointment?.patient_tel ?? "-"}
                  </Table.Cell>
                  <Table.Cell
                    style={{
                      textAlign: "left",
                      wordBreak: "break-all",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {user?.email ?? "-"}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: "left" }}>
                    {payment.patient_appointment?.extra?.address?.name ?? "-"}
                  </Table.Cell>
                  <Table.Cell>
                    {moment(payment.created).format("DD/MM/YYYY \\[HH:mm\\]") ?? "-"}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: "left" }}>
                    {moment(payment.created).add(1, "day").format("DD/MM/YYYY \\[HH:mm\\]") ?? "-"}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: "left" }}>
                    {payment.patient_appointment?.extra?.service?.name ?? "-"}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: "right" }}>
                    {payment.patient_appointment?.extra?.service?.price
                      ? Number(payment.patient_appointment.extra.service.price).toLocaleString(
                          "en-US",
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )
                      : "-"}
                  </Table.Cell>

                  {!isRequested && (
                    <>
                      <Table.Cell>
                        {moment(payment.patient_paid_at).format("DD/MM/YYYY \\[HH:mm\\]") ?? "-"}
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: "left" }}>
                        {fileUrl ? (
                          <span
                            onClick={() => {
                              setSelectedSlipUrl(fileUrl);
                              setOpenSlipPreview(true);
                            }}
                            style={{
                              color: COLOR.dark_blue,
                              cursor: "pointer",
                              wordBreak: "break-all",
                              whiteSpace: "normal",
                              display: "inline-block",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {fileName}
                          </span>
                        ) : (
                          "-"
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {payment.patient_appointment?.status === 5 &&
                        payment.status !== "REFUNDING" &&
                        payment.status !== "REFUNDED" ? (
                          <span
                            style={{
                              backgroundColor: "#aaeffb",
                              color: "#0c58cc",
                              padding: "4px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                              fontWeight: 500,
                            }}
                          >
                            รอคืนเงิน
                          </span>
                        ) : payment.status === "REFUNDING" ||
                          payment.status === "REQUESTED_REFUND" ? (
                          <span
                            style={{
                              backgroundColor: COLOR.light_orange,
                              color: "#d06500",
                              padding: "4px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                              fontWeight: 500,
                            }}
                          >
                            รับคำขอคืนเงิน
                          </span>
                        ) : payment.status === "REQUESTED_CONFIRM" ? (
                          <span
                            style={{
                              backgroundColor: COLOR.light_orange,
                              color: COLOR.orange,
                              padding: "4px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                              fontWeight: 500,
                            }}
                          >
                            แจ้งชำระ
                          </span>
                        ) : payment.status === "CONFIRMED" ? (
                          <span
                            style={{
                              backgroundColor: COLOR.light_green,
                              color: COLOR.green,
                              padding: "4px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                              fontWeight: 500,
                            }}
                          >
                            ชำระแล้ว
                          </span>
                        ) : payment.status === "REJECTED" ? (
                          <span
                            style={{
                              backgroundColor: "#ffd7d7",
                              color: "#ff0000",
                              padding: "4px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                              fontWeight: 500,
                            }}
                          >
                            หลักฐานมีปัญหา
                          </span>
                        ) : payment.status === "REFUNDED" ? (
                          <span
                            style={{
                              backgroundColor: COLOR.light_green,
                              color: COLOR.green,
                              padding: "4px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                              fontWeight: 500,
                            }}
                          >
                            คืนเงินสำเร็จ
                          </span>
                        ) : (
                          "-"
                        )}
                      </Table.Cell>
                    </>
                  )}
                  {!isNotChecked && (
                    <Table.Cell>
                      {payment.edited
                        ? moment(payment.edited).format("DD/MM/YYYY \\[HH:mm\\]")
                        : "-"}
                    </Table.Cell>
                  )}
                  {status === "REJECTED" && <Table.Cell>{payment.remark || "-"}</Table.Cell>}
                  {(isRefunding || isRefunded) && (
                    <>
                      <Table.Cell>
                        {payment.patient_appointment?.updated
                          ? moment(payment.patient_appointment?.updated).format(
                              "DD/MM/YYYY \\[HH:mm\\]"
                            )
                          : "-"}
                      </Table.Cell>
                      <Table.Cell>{payment.patient_appointment?.status_note || "-"}</Table.Cell>
                    </>
                  )}
                  {isRefunded && (
                    <>
                      <Table.Cell>
                        {payment.patient_appointment?.updated
                          ? moment(payment.edited).format("DD/MM/YYYY \\[HH:mm\\]")
                          : "-"}
                      </Table.Cell>
                      <Table.Cell>
                        {fileUrl ? (
                          <span
                            onClick={() => {
                              setSelectedSlipUrl(fileUrl);
                              setOpenSlipPreview(true);
                            }}
                            style={{ color: COLOR.dark_blue, cursor: "pointer" }}
                          >
                            {fileName}
                          </span>
                        ) : (
                          "-"
                        )}
                      </Table.Cell>
                    </>
                  )}
                  {addAction && (
                    <Table.Cell>
                      <Icon
                        name="edit"
                        size="large"
                        style={{ color: COLOR.orange, cursor: "pointer" }}
                        onClick={() => {
                          setSelectedPayment(payment);

                          if (isRefunding) {
                            setOpenRefundDetail(true);
                          } else {
                            setOpenSlipDetail(true);
                          }
                        }}
                      />
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}

            {emptyRows.map((_, idx) => (
              <Table.Row key={`empty-${idx}`} style={{ height: "50px" }}>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                <Table.Cell>&nbsp;</Table.Cell>
                {(isConfirm || status === "CONFIRMED") && (
                  <>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                  </>
                )}
                {status === "REJECTED" && (
                  <>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                  </>
                )}
                {isRefunding && (
                  <>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                  </>
                )}
                {status === "REFUNDED" && (
                  <>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                    <Table.Cell>&nbsp;</Table.Cell>
                  </>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <Modal
          open={openSlipPreview}
          className="card-slip-membership-modal"
          onClose={() => {
            setZoom(1);
            setOpenSlipPreview(false);
          }}
          basic
          closeOnDimmerClick
          size="fullscreen"
          mountNode={document.body}
          style={{ padding: 0, margin: 0 }}
        >
          <Icon
            name="close"
            size="large"
            color="black"
            circular
            style={{
              position: "absolute",
              top: "20px",
              left: "25px",
              cursor: "pointer",
              zIndex: 10,
              backgroundColor: "white",
            }}
            onClick={() => {
              setZoom(1);
              setOpenSlipPreview(false);
            }}
          />

          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "25px",
              display: "flex",
              gap: "15px",
              zIndex: 10,
            }}
          >
            <Icon
              name="search plus"
              size="large"
              style={{ cursor: "pointer" }}
              onClick={handleZoomIn}
            />
            <Icon
              name="search minus"
              size="large"
              style={{ cursor: "pointer" }}
              onClick={handleZoomOut}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              overflow: "auto",
            }}
          >
            <img
              src={selectedSlipUrl}
              alt="Payment Slip"
              style={{
                transform: `scale(${zoom})`,
                transition: "transform 0.2s ease-in-out",
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal>
        <Modal
          open={openSlipDetail}
          onClose={() => {
            setSelectedStatus("");
            setOpenSlipDetail(false);
            setSelectedPayment(null);
          }}
          size="large"
          style={{ padding: "20px" }}
        >
          <Modal.Content>
            {selectedPayment && (
              <>
                <h3 style={{ fontSize: "20px" }}>
                  ตรวจสอบรายละเอียดการชำระเงิน:
                  <span style={{ color: COLOR.primary, marginLeft: "10px" }}>
                    {`${selectedPayment.patient_appointment?.patient_first_name ?? ""} ${
                      selectedPayment.patient_appointment?.patient_last_name ?? ""
                    }`}
                  </span>
                </h3>
                <div
                  style={{
                    width: "100%",
                    height: "1px",
                    backgroundColor: COLOR.gray,
                    marginBottom: "2rem",
                  }}
                ></div>
                <Grid>
                  <Grid.Column
                    width={5}
                    textAlign="center"
                    style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                  >
                    <div
                      style={{
                        maxWidth: "100%",
                        maxHeight: "80vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={selectedPayment.payment_confirmation}
                        alt="Payment Slip Detail"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  </Grid.Column>

                  <Grid.Column width={11}>
                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>ชื่อบริการ</span>
                      <div style={boxStyle}>
                        {selectedPayment.patient_appointment?.extra?.service?.name || "-"}
                      </div>
                    </div>

                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>ยอดเงินโอน</span>
                      <div style={boxStyle}>
                        {selectedPayment.patient_appointment?.extra?.service?.price
                          ? Number(
                              selectedPayment.patient_appointment.extra.service.price
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : "-"}
                      </div>
                    </div>

                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>วันที่และเวลาที่ผู้รับบริการโอน</span>
                      <div
                        style={{
                          ...boxStyle,
                          width: "335px",
                          marginRight: "10px",
                          textAlign: "center",
                        }}
                      >
                        {selectedPayment.patient_paid_at
                          ? moment(selectedPayment.patient_paid_at).format("DD/MM/YYYY")
                          : "-"}
                      </div>
                      <div style={{ ...boxStyle, width: "220px", textAlign: "center" }}>
                        {selectedPayment.patient_paid_at
                          ? moment(selectedPayment.patient_paid_at).format("HH:mm")
                          : "-"}
                      </div>
                    </div>

                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>
                        สถานะการตรวจสอบ <span style={{ color: "red" }}>*</span>
                      </span>
                      <Dropdown
                        search
                        selection
                        placeholder="--เลือกตัวเลือก--"
                        options={STATUS_OPTIONS}
                        style={{ width: "590px" }}
                        value={selectedStatus || ""}
                        onChange={(e, { value }: any) => setSelectedStatus(value)}
                      />
                    </div>

                    {selectedStatus === "REJECT" && (
                      <div
                        key="reject-field"
                        style={{
                          ...fieldContainerStyle,
                          alignItems: "flex-start",
                        }}
                      >
                        <span style={labelStyle}>
                          สาเหตุที่มีปัญหา <span style={{ color: "red" }}>*</span>
                        </span>
                        <textarea
                          value={rejectReasonText}
                          onChange={(e) => setRejectReasonText(e.target.value)}
                          style={{
                            ...boxStyle,
                            height: "100px",
                            resize: "none",
                            textAlign: "left",
                            fontSize: "1em",
                            backgroundColor: "transparent",
                          }}
                          placeholder="กรอกสาเหตุที่มีปัญหา"
                          required
                        />
                      </div>
                    )}
                  </Grid.Column>
                </Grid>
              </>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                paddingTop: "1.5rem",
                fontSize: "20px",
              }}
            >
              <Button
                onClick={() => {
                  setSelectedStatus("");
                  setOpenSlipDetail(false);
                  setSelectedPayment(null);
                }}
              >
                ปิดหน้าต่าง
              </Button>
              <Button
                style={{
                  backgroundColor: COLOR.primary,
                  color: "white",
                }}
                loading={buttonLoadCheck?.postPaymentAction === "LOADING"}
                onClick={() =>
                  onEvent({
                    message: "HandlePostAppointmentPaymentRelationAction",
                    params: {
                      pk: selectedPayment.id,
                      status: selectedStatus,
                      reasonText: rejectReasonText,
                      division: selectedPayment.patient_appointment?.division,
                    },
                  })
                }
              >
                บันทึกข้อมูล
              </Button>
            </div>
          </Modal.Content>
        </Modal>
        <Modal
          open={openRefundDetail}
          onClose={() => {
            setSelectedRefundStatus("");
            setOpenRefundDetail(false);
            setSelectedPayment(null);
            setRefundDate("");
            setRefundTime("");
            setRejectReasonText("");
          }}
          size="large"
          style={{ padding: "20px" }}
        >
          <Modal.Content>
            {selectedPayment && (
              <>
                <h3 style={{ fontSize: "20px" }}>
                  ตรวจสอบรายละเอียดการขอคืนเงิน:
                  <span style={{ color: COLOR.primary, marginLeft: "10px" }}>
                    {`${selectedPayment.patient_appointment?.patient_first_name ?? ""} ${
                      selectedPayment.patient_appointment?.patient_last_name ?? ""
                    }`}
                  </span>
                </h3>
                <div
                  style={{
                    width: "100%",
                    height: "1px",
                    backgroundColor: COLOR.gray,
                    marginBottom: "2rem",
                  }}
                ></div>
                <Grid>
                  <Grid.Column
                    width={5}
                    textAlign="center"
                    style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                  >
                    <div
                      style={{
                        minWidth: "300px",
                        minHeight: "300px",
                        maxWidth: "100%",
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        border: "2px solid #c9c9c9",
                        borderRadius: "12px",
                        padding: "20px",
                        backgroundColor: "#fafafa",
                        transition: "border 0.2s ease",
                      }}
                      onClick={() => document.getElementById("refund-proof-upload")?.click()}
                    >
                      <input
                        id="refund-proof-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const imageUrl = URL.createObjectURL(file);
                            setSelectedPayment((prev: any) => ({
                              ...prev,
                              refund_proof_preview: imageUrl,
                              refund_proof_file: file,
                            }));
                          }
                        }}
                      />

                      {selectedPayment?.refund_proof_preview ? (
                        <img
                          src={selectedPayment.refund_proof_preview}
                          alt="Refund Proof"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "70vh",
                            objectFit: "contain",
                            borderRadius: "8px",
                          }}
                        />
                      ) : (
                        <>
                          <img
                            src={ICON.IconUpload}
                            alt="Upload Icon"
                            style={{
                              width: "80px",
                              height: "80px",
                              marginBottom: "10px",
                              opacity: 0.6,
                            }}
                          />
                          <span style={{ color: "#666", fontSize: "16px" }}>
                            อัปโหลดหลักฐานการคืนเงิน
                          </span>
                        </>
                      )}
                    </div>
                  </Grid.Column>

                  <Grid.Column width={11}>
                    <div
                      style={{
                        ...boxStyle,
                        color: "red",
                        textAlign: "left",
                        marginBottom: "2rem",
                        backgroundColor: COLOR.light_pink,
                        width: "100%",
                      }}
                    >
                      สาเหตุที่ยกเลิกนัดหมาย:{" "}
                      <span style={{ color: "black" }}>
                        {selectedPayment.patient_appointment?.status_note || "-"}
                      </span>
                    </div>
                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>ชื่อบริการ</span>
                      <div style={boxStyle}>
                        {selectedPayment.patient_appointment?.extra?.service?.name || "-"}
                      </div>
                    </div>

                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>ยอดเงินโอน</span>
                      <div style={boxStyle}>
                        {selectedPayment.patient_appointment?.extra?.service?.price
                          ? Number(
                              selectedPayment.patient_appointment.extra.service.price
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : "-"}
                      </div>
                    </div>

                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>วันที่และเวลาที่ผู้รับบริการโอน</span>
                      <div
                        style={{
                          ...boxStyle,
                          width: "335px",
                          marginRight: "10px",
                          textAlign: "center",
                        }}
                      >
                        {selectedPayment.patient_paid_at
                          ? moment(selectedPayment.patient_paid_at).format("DD/MM/YYYY")
                          : "-"}
                      </div>
                      <div style={{ ...boxStyle, width: "220px", textAlign: "center" }}>
                        {selectedPayment.patient_paid_at
                          ? moment(selectedPayment.patient_paid_at).format("HH:mm")
                          : "-"}
                      </div>
                    </div>
                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>วันที่ขอคืนเงิน</span>
                      <div
                        style={{
                          ...boxStyle,
                          width: "335px",
                          marginRight: "10px",
                          textAlign: "center",
                        }}
                      >
                        {selectedPayment.patient_appointment?.updated
                          ? moment(selectedPayment.patient_appointment?.updated).format(
                              "DD/MM/YYYY"
                            )
                          : "-"}
                      </div>
                      <div style={{ ...boxStyle, width: "220px", textAlign: "center" }}>
                        {selectedPayment.patient_appointment?.updated
                          ? moment(selectedPayment.patient_appointment?.updated).format("HH:mm")
                          : "-"}
                      </div>
                    </div>

                    <div style={fieldContainerStyle}>
                      <span style={labelStyle}>
                        สถานะการตรวจสอบการคืนเงิน <span style={{ color: "red" }}>*</span>
                      </span>
                      <Dropdown
                        search
                        selection
                        placeholder="--เลือกตัวเลือก--"
                        options={REFUND_STATUS_OPTIONS}
                        style={{ width: "590px" }}
                        value={selectedRefundStatus || ""}
                        onChange={(e, { value }: any) => setSelectedRefundStatus(value)}
                      />
                    </div>

                    {selectedRefundStatus === "REFUND" && (
                      <div
                        key="refund-field"
                        style={{
                          ...fieldContainerStyle,
                          alignItems: "center",
                          flexDirection: "row",
                          justifyContent: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <span style={{ ...labelStyle, width: "346px" }}>
                          วันที่โอนคืนผู้รับบริการ <span style={{ color: "red" }}>*</span>
                        </span>
                        <DateTextBox
                          value={refundDate}
                          onChange={(val: any) => setRefundDate(val)}
                          placeholder="เลือกวันที่โอนคืน"
                          inputStyle={{ maxWidth: "186px" }}
                        />
                        <div style={{ width: "125px", position: "relative" }}>
                          <style>
                            {`
                            .ui.search.selection.dropdown.icon {
                              width: 100% !important;
                              min-width: unset !important;
                              max-width: none !important;
                            }
                            .ui.search.selection.dropdown.icon > input.search {
                              width: 100% !important;
                              max-width: none !important;
                            }
                          `}
                          </style>
                          <TimeTextBox
                            value={refundTime || "00:00"}
                            onTimeChange={((_: any, val: string) => setRefundTime(val)) as any}
                            style={{ width: "100%" }}
                            interval={1}
                            minHour={0}
                            maxHour={23}
                            useDefaultTime={true}
                            clearable
                            search
                          />
                        </div>
                      </div>
                    )}
                    {selectedRefundStatus === "REJECT_REQUEST_REFUND" && (
                      <div
                        key="reject-field"
                        style={{
                          ...fieldContainerStyle,
                          alignItems: "flex-start",
                        }}
                      >
                        <span style={labelStyle}>
                          สาเหตุที่ปฏิเสธ <span style={{ color: "red" }}>*</span>
                        </span>
                        <textarea
                          value={rejectReasonText}
                          onChange={(e) => setRejectReasonText(e.target.value)}
                          style={{
                            ...boxStyle,
                            height: "100px",
                            resize: "none",
                            textAlign: "left",
                            fontSize: "1em",
                            backgroundColor: "transparent",
                          }}
                          placeholder="กรอกสาเหตุที่ปฏิเสธ"
                          required
                        />
                      </div>
                    )}
                  </Grid.Column>
                </Grid>
              </>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                paddingTop: "1.5rem",
                fontSize: "20px",
              }}
            >
              <Button
                onClick={() => {
                  setSelectedRefundStatus("");
                  setOpenRefundDetail(false);
                  setSelectedPayment(null);
                  setRefundDate("");
                  setRefundTime("");
                  setRejectReasonText("");
                }}
              >
                ปิดหน้าต่าง
              </Button>
              <Button
                style={{
                  backgroundColor: COLOR.primary,
                  color: "white",
                }}
                loading={buttonLoadCheck?.postPaymentAction === "LOADING"}
                onClick={() =>
                  onEvent({
                    message: "HandlePostAppointmentPaymentRelationAction",
                    params: {
                      pk: selectedPayment.id,
                      status: selectedRefundStatus,
                      reasonText: rejectReasonText,
                      division: selectedPayment.patient_appointment?.division,
                    },
                  })
                }
              >
                บันทึกข้อมูล
              </Button>
            </div>
          </Modal.Content>
        </Modal>
      </div>
    );
  }
);

const CardPaymentSlip: React.FC<CardPaymentSlipProps> = (props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [filteredPayment, setFilteredPayment] = useState<any[]>([]);
  const [activePage, setActivePage] = useState(1);

  const [openSlipDetail, setOpenSlipDetail] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [rejectReasonText, setRejectReasonText] = useState<string>("");
  const [selectedPatientId, setSelectedPatientId] = useState<string | undefined>(undefined);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [filterDueDateStart, setFilterDueDateStart] = useState<string | undefined>(undefined);
  const [filterDueDateEnd, setFilterDueDateEnd] = useState<string | undefined>(undefined);
  const [filterPatientPaidStart, setFilterPatientPaidStart] = useState<string | undefined>(
    undefined
  );
  const [filterPatientPaidEnd, setFilterPatientPaidEnd] = useState<string | undefined>(undefined);
  const [filterCheckPaymentStart, setFilterCheckPaymentStart] = useState<string | undefined>(
    undefined
  );
  const [filterCheckPaymentEnd, setFilterCheckPaymentEnd] = useState<string | undefined>(undefined);
  const [filterRequestRefundStart, setFilterRequestRefundStart] = useState<string | undefined>(
    undefined
  );
  const [filterRequestRefundEnd, setFilterRequestRefundEnd] = useState<string | undefined>(
    undefined
  );
  const [selectedAppointmentStatuses, setSelectedAppointmentStatuses] = useState<string[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(undefined);
  const [filterAppointmentStart, setFilterAppointmentStart] = useState<string | undefined>(
    undefined
  );
  const [filterAppointmentEnd, setFilterAppointmentEnd] = useState<string | undefined>(undefined);
  const [masterPaymentList, setMasterPaymentList] = useState<any[]>([]);
  const [displayedPaymentList, setDisplayedPaymentList] = useState<any[]>([]);

  const tableRef = useRef<any>(null);

  const handleTabChange = (e: any, { activeIndex }: any) => {
    setFilterName("");
    setFilterDueDateStart(undefined);
    setFilterDueDateEnd(undefined);
    setFilterPatientPaidStart(undefined);
    setFilterPatientPaidEnd(undefined);
    setFilterCheckPaymentStart(undefined);
    setFilterCheckPaymentEnd(undefined);
    setFilterRequestRefundStart(undefined);
    setFilterRequestRefundEnd(undefined);
    setSelectedAppointmentStatuses([]);
    setSelectedDoctorId(undefined);
    setFilterAppointmentStart(undefined);
    setFilterAppointmentEnd(undefined);
    setActivePage(1);

    if (tableRef.current?.resetColumnWidths) {
      tableRef.current.resetColumnWidths();
    }

    props.onEvent({
      message: "HandleGetAppointmentPaymentRelationList",
    });
    setActiveTabIndex(activeIndex);
  };

  const handlePageChange = (_e: any, { activePage }: any) => {
    setActivePage(activePage);
  };

  const getPaginatedData = (
    list: any[],
    statuses: (string | number)[] | string | number,
    excludePatientStatus5: boolean = false,
    excludeRefunded: boolean = false
  ) => {
    const statusArray = Array.isArray(statuses) ? statuses : [statuses];

    const filtered = list.filter((p: any) => {
      const appointmentStatus = p.patient_appointment?.status;

      if (excludePatientStatus5 && appointmentStatus === 5) return false;

      if (excludeRefunded && p.status === "REFUNDED") return false;

      const statusMatch = statusArray.includes(p.status);
      const patientAppointmentMatch = appointmentStatus
        ? statusArray.includes(appointmentStatus)
        : false;

      return statusMatch || patientAppointmentMatch;
    });

    const data = filtered.slice((activePage - 1) * ROWS_PER_PAGE, activePage * ROWS_PER_PAGE);
    const emptyRowsCount = ROWS_PER_PAGE - data.length;
    const emptyRows = Array.from({ length: Math.max(emptyRowsCount, 0) });
    const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));

    return { filtered, data, emptyRows, totalPages };
  };

  const {
    filtered: requested,
    data: requestedData,
    emptyRows: requestedEmptyRows,
    totalPages: totalRequestedPages,
  } = getPaginatedData(filteredPayment, "REQUESTED", true);

  const {
    filtered: requestedConfirm,
    data: requestedConfirmData,
    emptyRows: requestedConfirmEmptyRows,
    totalPages: totalRequestedConfirmPages,
  } = getPaginatedData(filteredPayment, "REQUESTED_CONFIRM", true);

  const {
    filtered: confirmed,
    data: confirmedData,
    emptyRows: confirmedEmptyRows,
    totalPages: totalConfirmedPages,
  } = getPaginatedData(filteredPayment || [], "CONFIRMED", true);

  const {
    filtered: rejected,
    data: rejectedData,
    emptyRows: rejectedEmptyRows,
    totalPages: totalRejectedPages,
  } = getPaginatedData(filteredPayment || [], "REJECTED", true);

  const {
    filtered: requestedRefund,
    data: requestedRefundData,
    emptyRows: requestedRefundEmptyRows,
    totalPages: totalRequestedRefundPages,
  } = getPaginatedData(filteredPayment || [], ["REQUESTED_REFUND", "REFUNDING", 5], false, true);

  const {
    filtered: refunded,
    data: refundedData,
    emptyRows: refundedEmptyRows,
    totalPages: totalRefundedPages,
  } = getPaginatedData(filteredPayment || [], "REFUNDED");

  const handleSearchPaymentClick = () => {
    const formatDate = (dateStr?: string, isEnd = false) => {
      if (!dateStr) return undefined;
      const date = new Date(dateStr);
      if (isEnd) date.setHours(23, 59, 59, 999);
      return date.toISOString().split("T")[0];
    };

    props.onEvent({
      message: "HandleFilterAppointmentPaymentRelationList",
      params: {
        patient: selectedPatientId,
        statuses: selectedStatuses.length ? selectedStatuses : undefined,
        due_date_start: formatDate(filterDueDateStart),
        due_date_end: formatDate(filterDueDateEnd, true),
        patient_paid_date_start: formatDate(filterPatientPaidStart),
        patient_paid_date_end: formatDate(filterPatientPaidEnd, true),
        check_payment_date_start: formatDate(filterCheckPaymentStart),
        check_payment_date_end: formatDate(filterCheckPaymentEnd, true),
        request_refund_date_start: formatDate(filterRequestRefundStart),
        request_refund_date_end: formatDate(filterRequestRefundEnd, true),
        patient_appointment_statuses: selectedAppointmentStatuses.length
          ? selectedAppointmentStatuses
          : undefined,
        doctor: selectedDoctorId,
        patient_appointment_start_date: formatDate(filterAppointmentStart),
        patient_appointment_end_date: formatDate(filterAppointmentEnd, true),
      },
    });

    setActivePage(1);
  };

  const filterPayment = useMemo(() => {
    let list = [...masterPaymentList];

    if (filterName?.trim()) {
      const lowerName = filterName.toLowerCase();
      list = list.filter((p: any) => {
        const firstName = p.patient_appointment?.patient_first_name?.toLowerCase() || "";
        const lastName = p.patient_appointment?.patient_last_name?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`.trim();

        return (
          firstName.includes(lowerName) ||
          lastName.includes(lowerName) ||
          fullName.includes(lowerName)
        );
      });
    }

    return list;
  }, [masterPaymentList, filterName]);

  const FilterDate = ({
    value,
    onChange,
    placeholder = "dd/mm/yyyy",
  }: {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
  }) => {
    const [localValue, setLocalValue] = React.useState(
      value ? moment(value).add(543, "years").format("DD/MM/YYYY") : ""
    );

    useEffect(() => {
      setLocalValue(value ? moment(value).add(543, "years").format("DD/MM/YYYY") : "");
    }, [value]);

    return (
      <DateTextBox
        placeholder={placeholder}
        value={localValue}
        onChange={(val) => {
          setLocalValue(val);

          const isFullDate = /^\d{2}\/\d{2}\/\d{4}$/.test(val);
          if (!isFullDate) return;

          const date = parseBeOrAdDate(val);
          if (date && date.isValid()) {
            onChange(date.format("YYYY-MM-DD"));
          }
        }}
      />
    );
  };

  useEffect(() => {
    props.onEvent({
      message: "HandleGetAppointmentPaymentRelationList",
    });
  }, []);

  useEffect(() => {
    const initialData = (props.appointmentPaymentRelationList || []).filter(
      (p: any) => p.status === "REQUESTED"
    );
    setFilteredPayment(initialData);
  }, [props.appointmentPaymentRelationList]);

  useEffect(() => {
    if (props.buttonLoadCheck?.postPaymentAction === "SUCCESS") {
      setOpenSlipDetail(false);
      setSelectedStatus("");
      setRejectReasonText("");
    }
  }, [props.buttonLoadCheck?.postPaymentAction]);

  useEffect(() => {
    if (props.appointmentPaymentRelationList?.length) {
      setMasterPaymentList((prev) => {
        if (prev.length === 0) return props.appointmentPaymentRelationList;
        return prev;
      });

      setFilteredPayment(props.appointmentPaymentRelationList);
      setDisplayedPaymentList(props.appointmentPaymentRelationList);
    }
  }, [props.appointmentPaymentRelationList]);

  useEffect(() => {
    setFilteredPayment(filterPayment);
  }, [filterPayment]);

  useEffect(() => {
    if (props.appointmentPaymentRelationList?.length) {
      setMasterPaymentList([...props.appointmentPaymentRelationList]);
    } else {
      setMasterPaymentList([]);
    }
  }, [props.appointmentPaymentRelationList]);

  return (
    <div className="card-slip-membership" style={{ padding: "15px 0" }}>
      <Tab
        activeIndex={activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={[
          {
            menuItem: {
              key: "requested",
              content: (
                <div>
                  <span>รอชำระ</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) => p.status === "REQUESTED" && p.patient_appointment?.status !== 5
                      ).length
                    }
                  </div>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ marginBottom: "0.5rem" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ครบกำหนดชำระ</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterDueDateStart || ""}
                          onChange={setFilterDueDateStart}
                        />
                        <FilterDate value={filterDueDateEnd || ""} onChange={setFilterDueDateEnd} />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterDueDateStart(undefined);
                          setFilterDueDateEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>

                  <div style={{ fontWeight: "normal" }}>
                    <CustomTable
                      ref={tableRef}
                      data={requestedData}
                      emptyRows={requestedEmptyRows}
                      status="REQUESTED"
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRequestedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "requested confirm",
              content: (
                <div>
                  <span>แจ้งชำระ</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) =>
                          p.status === "REQUESTED_CONFIRM" && p.patient_appointment?.status !== 5
                      ).length
                    }
                  </div>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ marginBottom: "0.5rem" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ผู้รับบริการโอน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterPatientPaidStart || ""}
                          onChange={setFilterPatientPaidStart}
                        />
                        <FilterDate
                          value={filterPatientPaidEnd || ""}
                          onChange={setFilterPatientPaidEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterPatientPaidStart(undefined);
                          setFilterPatientPaidEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>

                  <div style={{ fontWeight: "normal" }}>
                    <CustomTable
                      ref={tableRef}
                      data={requestedConfirmData}
                      emptyRows={requestedConfirmEmptyRows}
                      status="REQUESTED_CONFIRM"
                      buttonLoadCheck={props.buttonLoadCheck}
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRequestedConfirmPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "confirmed",
              content: (
                <div>
                  <span>ชำระแล้ว</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) => p.status === "CONFIRMED" && p.patient_appointment?.status !== 5
                      ).length
                    }
                  </div>{" "}
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ marginBottom: "0.5rem" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ผู้รับบริการโอน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterPatientPaidStart || ""}
                          onChange={setFilterPatientPaidStart}
                        />
                        <FilterDate
                          value={filterPatientPaidEnd || ""}
                          onChange={setFilterPatientPaidEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ตรวจสอบหลักฐาน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterCheckPaymentStart || ""}
                          onChange={setFilterCheckPaymentStart}
                        />
                        <FilterDate
                          value={filterCheckPaymentEnd || ""}
                          onChange={setFilterCheckPaymentEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterPatientPaidStart(undefined);
                          setFilterPatientPaidEnd(undefined);
                          setFilterCheckPaymentStart(undefined);
                          setFilterCheckPaymentEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <CustomTable
                      ref={tableRef}
                      data={confirmedData}
                      emptyRows={confirmedEmptyRows}
                      status="CONFIRMED"
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalConfirmedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "rejected",
              content: (
                <div>
                  <span>หลักฐานมีปัญหา</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) => p.status === "REJECTED" && p.patient_appointment?.status !== 5
                      ).length
                    }
                  </div>{" "}
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ marginBottom: "0.5rem" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ผู้รับบริการโอน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterPatientPaidStart || ""}
                          onChange={setFilterPatientPaidStart}
                        />
                        <FilterDate
                          value={filterPatientPaidEnd || ""}
                          onChange={setFilterPatientPaidEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ตรวจสอบหลักฐาน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterCheckPaymentStart || ""}
                          onChange={setFilterCheckPaymentStart}
                        />
                        <FilterDate
                          value={filterCheckPaymentEnd || ""}
                          onChange={setFilterCheckPaymentEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterPatientPaidStart(undefined);
                          setFilterPatientPaidEnd(undefined);
                          setFilterCheckPaymentStart(undefined);
                          setFilterCheckPaymentEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <CustomTable
                      ref={tableRef}
                      data={rejectedData}
                      emptyRows={rejectedEmptyRows}
                      status="REJECTED"
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRejectedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "requested refund",
              content: (
                <div>
                  <span>รอคืนเงิน</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) =>
                          (p.status === "REQUESTED_REFUND" ||
                            p.status === "REFUNDING" ||
                            p.patient_appointment?.status === 5) &&
                          p.status !== "REFUNDED"
                      ).length
                    }
                  </div>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ marginBottom: "0.5rem" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ขอคืนเงิน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterRequestRefundStart || ""}
                          onChange={setFilterRequestRefundStart}
                        />
                        <FilterDate
                          value={filterRequestRefundEnd || ""}
                          onChange={setFilterRequestRefundEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterRequestRefundStart(undefined);
                          setFilterRequestRefundEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <CustomTable
                      ref={tableRef}
                      data={requestedRefundData}
                      emptyRows={requestedRefundEmptyRows}
                      status="REQUESTED_REFUND"
                      buttonLoadCheck={props.buttonLoadCheck}
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRequestedRefundPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "refunded",
              content: (
                <div>
                  <span>คืนเงินสำเร็จ</span>
                  <div className="badge">
                    {masterPaymentList.filter((p: any) => p.status === "REFUNDED").length}
                  </div>{" "}
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ marginBottom: "0.5rem" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ minWidth: "300px" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ขอคืนเงิน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterRequestRefundStart || ""}
                          onChange={setFilterRequestRefundStart}
                        />
                        <FilterDate
                          value={filterRequestRefundEnd || ""}
                          onChange={setFilterRequestRefundEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterRequestRefundStart(undefined);
                          setFilterRequestRefundEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <CustomTable
                      ref={tableRef}
                      data={refundedData}
                      emptyRows={refundedEmptyRows}
                      status="REFUNDED"
                      buttonLoadCheck={props.buttonLoadCheck}
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRefundedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
        ]}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default CardPaymentSlip;
